import {
  Transport,
  TransportPayload,
  useCreateTransportMutation,
  useDeleteTransportMutation,
  useUpdateTransportMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {ResultTypeFrom} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";

export type UseTransportMutationProps = {}

export type UseTransportMutationResult = {
  createTransport: (transport: TransportPayload, onSuccess: (() => void) | undefined) => void
  updateTransport: (transport: TransportPayload, onSuccess: (() => void) | undefined) => void
  deleteTransport: (id: string, onSuccess: (() => void) | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const useTransportMutation = (props: UseTransportMutationProps = {}): UseTransportMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createTransport, {isLoading: isCreating}] = useCreateTransportMutation()
  const [_updateTransport, {isLoading: isUpdating}] = useUpdateTransportMutation()
  const [_deleteTransport, {isLoading: isDeleting}] = useDeleteTransportMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useTransportUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.transport", "Transport"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useTransportUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useTransportUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.transport", "Transport"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useTransportUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useTransportUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.transport", "Transport"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useTransportUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.transport", "Transport") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.transport", "Transport") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.transport", "Transport") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteConfirm = useCallback((id: string, onSuccess: (() => void) | undefined = undefined) => {
    const promise = _deleteTransport({id: id}).unwrap()
    handleDeleteResult(promise, onSuccess)
  }, [_deleteTransport, handleDeleteResult])

  const createTransport = useCallback((transport: TransportPayload, onSuccess: (() => void) | undefined) => {
    const promise = _createTransport(transport).unwrap()
    handleCreateResult(promise, onSuccess)
  }, [_createTransport, handleCreateResult])

  const updateTransport = useCallback((transport: TransportPayload, onSuccess: (() => void) | undefined) => {
    const promise = _updateTransport(transport).unwrap()
    handleUpdateResult(promise, onSuccess)
  }, [_updateTransport, handleUpdateResult])

  const deleteTransport = useCallback((id: string, onSuccess: (() => void) | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.transport", "Transport"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.deletetransport.confirm", "Are you sure you want to delete this transport?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(id, onSuccess)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    createTransport: createTransport,
    updateTransport: updateTransport,
    deleteTransport: deleteTransport,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
